import React from 'react';
import { useLocation, useNavigate, useParams, type Location, type NavigateFunction } from 'react-router-dom';

type HistoryLike = {
  push: (to: string | { pathname?: string; state?: unknown }, state?: unknown) => void;
  replace: (to: string | { pathname?: string; state?: unknown }, state?: unknown) => void;
  goBack: () => void;
};

type InjectedRouterProps = {
  history: HistoryLike;
  location: Location;
  match: { params: ReturnType<typeof useParams> };
  navigate: NavigateFunction;
};

export function withRouter<P extends object> (Component: React.ComponentType<P>) {
  const WrappedComponent = (props: Omit<P, keyof InjectedRouterProps>) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const history: HistoryLike = {
      push: (to, state) => {
        if (typeof to === 'string') {
          navigate(to, state !== undefined ? { state } : undefined);
          return;
        }

        navigate(to.pathname ?? '/', { state: to.state ?? state });
      },
      replace: (to, state) => {
        if (typeof to === 'string') {
          navigate(to, { replace: true, ...(state !== undefined ? { state } : {}) });
          return;
        }

        navigate(to.pathname ?? '/', { replace: true, state: to.state ?? state });
      },
      goBack: () => {
        navigate(-1);
      }
    };

    return (
      <Component
        {...(props as P)}
        history={history}
        location={location}
        match={{ params }}
        navigate={navigate}
      />
    );
  };

  WrappedComponent.displayName = `withRouter(${Component.displayName ?? Component.name ?? 'Component'})`;

  return WrappedComponent as React.FC<Omit<P, keyof InjectedRouterProps>>;
}
